import React, { useEffect, useRef, useState } from "react";
import { offmarketApi } from "../../lib/api";
import { toast } from "sonner";
import { X, MapPin, Play, Star, Check, Loader2 } from "lucide-react";

const FINAL = ["SUCCEEDED", "FAILED", "ABORTED", "TIMED-OUT"];
const keyOf = (r) => `${(r.name || "").toLowerCase()}|${(r.phone || "").replace(/\D/g, "") || (r.address || "").toLowerCase()}`;

// Apify Google Maps scrape across one or many cities: one run per city, polled together, merged + de-duplicated.
export const ApifyScrapeModal = ({ open, onClose, defaults, configured, onSave }) => {
  const [q, setQ] = useState("");
  const [cities, setCities] = useState("");
  const [max, setMax] = useState(40);
  const [runs, setRuns] = useState([]);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (open) { setQ(defaults?.query || ""); setCities(defaults?.location || ""); setRows([]); setSel({}); setRuns([]); setElapsed(0); }
    return () => clearInterval(timer.current);
  }, [open]); // eslint-disable-line

  const pending = runs.filter((r) => !FINAL.includes(r.status));
  useEffect(() => {
    if (!runs.length || !pending.length) return;
    const started = Date.now();
    timer.current = setInterval(async () => {
      setElapsed(Math.round((Date.now() - started) / 1000));
      const updated = await Promise.all(runs.map(async (r) => {
        if (FINAL.includes(r.status)) return r;
        try {
          const p = await offmarketApi.apifyPoll(r.runId);
          return FINAL.includes(p.status) ? { ...r, status: p.status, items: p.items || [], usd: p.usd || 0 } : { ...r, status: p.status };
        } catch (e) { return { ...r, status: "FAILED", items: [], error: e?.response?.data?.detail || "failed" }; }
      }));
      setRuns(updated);
      if (updated.every((r) => FINAL.includes(r.status))) {
        clearInterval(timer.current);
        const seen = new Set(); const merged = [];
        updated.forEach((r) => (r.items || []).forEach((it) => { const k = keyOf(it); if (!seen.has(k)) { seen.add(k); merged.push({ ...it, scrapedCity: r.location }); } }));
        setRows(merged);
        const all = {}; merged.forEach((_, i) => { all[i] = true; }); setSel(all);
        const usd = updated.reduce((s, r) => s + (Number(r.usd) || 0), 0);
        const failed = updated.filter((r) => r.status !== "SUCCEEDED").length;
        toast.success(`${merged.length} unique places across ${updated.length} cit${updated.length === 1 ? "y" : "ies"}${usd ? ` · $${usd.toFixed(2)}` : ""}${failed ? ` · ${failed} run(s) failed` : ""}`);
      }
    }, 4000);
    return () => clearInterval(timer.current);
  }, [runs.map((r) => r.runId).join(",")]); // eslint-disable-line

  if (!open) return null;
  const running = pending.length > 0;
  const cityList = cities.split(/[\n;]/).map((s) => s.trim()).filter(Boolean);
  const start = async () => {
    if (!q.trim() || !cityList.length) return toast.error("Add a search term and at least one city");
    try {
      const r = await offmarketApi.apifyStart({ query: q, locations: cityList.slice(0, 8), maxPlaces: Number(max) || 40 });
      setRows([]); setSel({}); setElapsed(0); setRuns((r.runs || []).map((x) => ({ ...x, status: x.status || "RUNNING" })));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the scrape"); }
  };
  const picked = rows.filter((_, i) => sel[i]);
  const save = async () => {
    if (!picked.length) return toast.error("Tick at least one place");
    setSaving(true);
    try { await onSave(picked); onClose(); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" data-testid="apify-modal" onMouseDown={onClose}>
      <div className="modal-card" style={{ maxWidth: 940, width: "96%", maxHeight: "92vh", overflowY: "auto" }} onMouseDown={(e) => e.stopPropagation()}>
        <button className="modal-x" onClick={onClose} aria-label="Close" data-testid="apify-close"><X size={14} /></button>
        <div className="modal-title"><MapPin size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Google Maps scrape (Apify)</div>
        <div className="modal-sub">Scrapes every matching business on Google Maps for a trade across one or many cities — name, website, phone, address, rating and reviews. One run per city, merged and de-duplicated. Roughly $1.50 per 1,000 places.</div>
        {!configured && <div className="ub-banner" data-testid="apify-not-configured"><span><b>Apify is not connected.</b> Add <code>APIFY_API_TOKEN</code> to the backend .env and restart.</span></div>}
        <div className="om-apollo-form">
          <input className="inp" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search terms, comma separated — landscaping company, lawn care" data-testid="apify-query" />
          <select className="inp" value={max} onChange={(e) => setMax(e.target.value)} style={{ width: 150 }} data-testid="apify-max">
            {[20, 40, 80, 120, 200].map((n) => <option key={n} value={n}>{n} places / city</option>)}
          </select>
          <button className="btn btn-primary" onClick={start} disabled={running || !configured} data-testid="apify-run"><Play size={13} />{running ? "Scraping…" : `Scrape ${cityList.length || ""} cit${cityList.length === 1 ? "y" : "ies"}`}</button>
        </div>
        <div className="field" style={{ marginTop: 8 }}>
          <label>Cities — one per line (or separated by ;). Up to 8 per scrape.</label>
          <textarea className="inp" rows={3} value={cities} onChange={(e) => setCities(e.target.value)} placeholder={"McKinney, TX, USA\nFrisco, TX, USA\nPlano, TX, USA"} data-testid="apify-cities" />
        </div>
        {runs.length > 0 && (
          <div className="om-runs" data-testid="apify-progress">
            {runs.map((r) => (
              <span key={r.runId} className={`om-run ${FINAL.includes(r.status) ? (r.status === "SUCCEEDED" ? "ok" : "bad") : ""}`}>
                {FINAL.includes(r.status) ? <Check size={11} /> : <Loader2 size={11} className="spin" />}{r.location}{r.items ? ` · ${r.items.length}` : ""}
              </span>
            ))}
            {running && <span className="om-sub">{elapsed}s · larger areas take 1–3 minutes</span>}
          </div>
        )}
        {rows.length > 0 && (
          <>
            <div className="cm-empty" style={{ margin: "8px 0 4px" }}>{rows.length} unique places · {picked.length} selected</div>
            <table className="tbl" data-testid="apify-results">
              <thead><tr><th style={{ width: 26 }}><input type="checkbox" checked={rows.every((_, i) => sel[i])} onChange={() => { const all = rows.every((_, i) => sel[i]); const n = {}; if (!all) rows.forEach((_, i) => { n[i] = true; }); setSel(n); }} data-testid="apify-all" /></th><th>Business</th><th>Category</th><th>Address</th><th>Phone</th><th>Rating</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} data-testid={`apify-row-${i}`}>
                    <td><input type="checkbox" checked={!!sel[i]} onChange={() => setSel((s) => ({ ...s, [i]: !s[i] }))} data-testid={`apify-check-${i}`} /></td>
                    <td><b>{r.name}</b>{r.website ? <div><a href={r.website} target="_blank" rel="noreferrer" style={{ fontSize: 10.5, color: "var(--lime)" }}>{r.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}</a></div> : <div className="om-sub">no website</div>}</td>
                    <td style={{ fontSize: 11, color: "var(--text2)" }}>{r.services || "—"}</td>
                    <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{[r.address, r.city, r.state].filter(Boolean).join(", ") || r.scrapedCity || "—"}</td>
                    <td style={{ fontSize: 11, fontFamily: "var(--mono)", whiteSpace: "nowrap" }}>{r.phone || "—"}</td>
                    <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{r.reviews ? <><Star size={10} style={{ color: "var(--lime)", verticalAlign: -1, marginRight: 3 }} />{r.reviews.replace(" Google reviews", "")}</> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="modal-actions">
              <button className="btn" onClick={onClose} data-testid="apify-cancel">Close</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !picked.length} data-testid="apify-save">{saving ? "Saving…" : `Save ${picked.length} as targets`}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
